import { styled } from "styled-components";
import DefaultChatMessage from "./chat-default-messages/DefaultChatMessage";
import ChatInput from "./ChatInput";
import ChatQuestions from "./ChatQuestions";
import { useState, useEffect, useRef } from "react";
import ChatLoading from "./ChatLoading";
import ChatResponse from "./ChatResponse";
import {
  CreateMessages,
  IPageableResponse,
  IDataSource,
  IDataSourceFilter,
  IPaginationFilters,
  IMessagePayload,
  chatType,
  dataSourcesInfo,
  scrollBarStyles,
  IConversation,
  IConversationMessage,
  getDataSources,
  QUERIES,
  openNotification,
  Loading as LoadingComponent,
} from "@dataone/lib";
import { UseQueryResult, useMutation, useQuery } from "@tanstack/react-query";
import { Drawer } from "antd";
import ChatDatasourceHeader from "./chat-header/ChatDatasourceHeader";
import { Loading } from "../Loading";
import DatasourceDefaultChatMessage from "./chat-default-messages/DatasourceDefaultChatMessage";
import ChatDatasourcesList from "./ChatDatasourcesList";
import { useAuth } from "../../../../auth/contexts/auth.provider";

// const datasetsFilters: IPaginationFilters<IDataSourceFilter, IDataSource> = {
//   body: { displayName: "" },
//   pageNo: 0,
//   pageSize: 50,
//   beforeDate: new Date(),
//   sortBy: "createdAt",
//   sort: "DESC",
// };

interface DiscussionSearchProps {
  stepRef6: React.MutableRefObject<null>;
  stepRef7: React.MutableRefObject<null>;
  isNewDiscussionStarted: boolean;
  conversation?: IPageableResponse<IConversationMessage>;
  discussionHistories?: IPageableResponse<IConversation>;
  selectedConversation?: IConversation;
  messagePayLoad: IMessagePayload;
  selectedOption: number;
  selectedDatasources: dataSourcesInfo[];
  isLoadingConversation: boolean;
  fixState: () => void;
  setConversation?: React.Dispatch<
    React.SetStateAction<IPageableResponse<IConversationMessage>>
  >;
  refetchHistory: (options?: {
    throwOnError: boolean;
    cancelRefetch: boolean;
  }) => Promise<UseQueryResult>;
  setMessagePayLoad: (message: IMessagePayload) => void;
  setSelectedOption: (option: number) => void;
  setSelectedDatabase?: (database: IDataSource) => void;
  setSelectedDatasources: React.Dispatch<
    React.SetStateAction<dataSourcesInfo[]>
  >;
  // setDatasetsFilters?: React.Dispatch<
  //   React.SetStateAction<IPaginationFilters<IDataSourceFilter, IDataSource>>
  // >;
  isLoadingMore?: boolean;
  messagesRef?: React.MutableRefObject<HTMLDivElement | null>;
  bottomMessagesRef?: React.MutableRefObject<HTMLElement | null>;
  reachTheEndRef?: React.MutableRefObject<HTMLElement | null>;
  handleRefetch?: (pageNo: number, itemAdded?: boolean) => void;
  historyCollapsed?: boolean;
}
const DiscussionSearch = ({
  stepRef6,
  stepRef7,
  isNewDiscussionStarted,
  conversation,
  discussionHistories,
  selectedConversation,
  messagePayLoad,
  selectedOption,
  selectedDatasources,
  setConversation,
  refetchHistory,
  setMessagePayLoad,
  setSelectedOption,
  setSelectedDatabase,
  setSelectedDatasources,
  fixState,
  // setDatasetsFilters,
  isLoadingMore,
  messagesRef,
  bottomMessagesRef,
  reachTheEndRef,
  handleRefetch,
  isLoadingConversation,
  historyCollapsed,
}: DiscussionSearchProps) => {
  const [messages, setMessages] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean[]>([]);
  const [responses, setResponses] = useState<boolean[]>([]);
  const [showDatabases, setShowDatabases] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const chatDatasourcesRef = useRef<HTMLDivElement>(null);

  // const [selectedDatabase, setSelectedDatabase] = useState<IDatabase>();
  // const [selectedWebsite, setSelectedWebsite] = useState<string>("");

  const [datasetsFilters, setDatasetsFilters] = useState<
    IPaginationFilters<IDataSourceFilter, IDataSource>
  >({
    body: { displayName: "" },
    pageNo: 0,
    pageSize: 50,
    beforeDate: new Date(),
    sortBy: "createdAt",
    sort: "DESC",
  });

  const { setUser } = useAuth();

  const { mutate: addMessage } = useMutation(CreateMessages);
  const { data: datasources, isLoading: IsLoading } = useQuery({
    queryKey: [QUERIES.DATA_SETS, datasetsFilters],
    queryFn: () => getDataSources(datasetsFilters),
  });

  const containerRef = useRef<HTMLDivElement>(null);

  const showDefaultChatMessage =
    conversation?.content?.length === 0 ||
    (Array.isArray(conversation?.content) &&
      conversation?.content.every((item) => item === undefined));
  const handleMessageSend = (message: string) => {
    if (message.trim() !== "") {
      setLoading((prevLoading) => [...prevLoading, true]);
      setConversation?.((prevConversation) => ({
        ...prevConversation,
        // pageNo: 0,
        content: [{ input: message, id: "1" }, ...prevConversation.content],
      }));
      addMessage(
        {
          conversationId: showDefaultChatMessage
            ? ""
            : selectedConversation?.id !== undefined
            ? selectedConversation?.id
            : discussionHistories?.content[0]?.id,
          input: message,
          chat: messagePayLoad.chat,
          dataSources: selectedDatasources.map((datasource) => datasource.id!),
        },
        {
          onSuccess: (response) => {
            const reader = response?.body?.getReader();
            var decoder = new TextDecoder("utf-8");
            let isDecreasedMessage = false;
            reader?.read().then(function processResult(result): any {
              if (result.done) return;
              let token = decoder.decode(result.value);
              setConversation?.((prevConversation) => {
                const updatedContent = [...prevConversation.content];
                try {
                  const errorCheck = JSON.parse(token);
                  if (errorCheck.message) {
                    openNotification(
                      errorCheck.message,
                      errorCheck.message ===
                        "Your subscription has expired. Please proceed to reactivate it."
                        ? "info"
                        : "error"
                    );
                    delete updatedContent[0];
                    return {
                      ...prevConversation,
                      content: updatedContent,
                    };
                  }
                } catch (err) {
                  if (!isDecreasedMessage) {
                    isDecreasedMessage = true;
                    setUser((prev) =>
                      prev?.subscription
                        ? {
                            ...prev,
                            subscription: {
                              ...prev.subscription,
                              chatUsage: prev.subscription.chatUsage - 1,
                            },
                          }
                        : prev
                    );
                  }
                }
                if (updatedContent.length > 0) {
                  const element = updatedContent[0];
                  if (
                    element.response !== undefined &&
                    element.response.endsWith(token)
                  ) {
                    return prevConversation;
                  }
                  if (element.response === undefined) {
                    element.response = token;
                  } else {
                    element.response += token;
                  }
                }

                return {
                  ...prevConversation,
                  // pageNo: 0,
                  content: updatedContent,
                };
              });
              return reader.read().then(processResult);
            });
            setLoading((prevLoading) => [...prevLoading.slice(0, -1), false]);
            handleRefetch?.(0, true);
          },
        }
      );
    }
  };

  useEffect(() => {
    if (isNewDiscussionStarted) {
      setMessages([]);
      setLoading([]);
      setResponses([]);
      fixState();
      setConversation?.((prevConversation) => ({
        last: true,
        totalPages: 1,
        totalElements: 0,
        pageSize: 0,
        pageNo: 0,
        content: [],
      }));
    }
  }, [setMessages, fixState, isNewDiscussionStarted]);

  // useEffect(() => {
  //   if (containerRef.current) {
  //     containerRef.current.scrollTop = containerRef.current.scrollHeight;
  //     // window.scrollTo(0, document.body.scrollHeight);
  //   }
  // }, [conversation?.content]);
  const handleShowDatabases = (value?: boolean) => {
    if (value !== undefined) {
      setShowDatabases(value);
    } else setShowDatabases(!showDatabases);
  };
  const handleChangeWebsite = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMessagePayLoad({
      ...messagePayLoad,
      website: e?.target?.value,
    });
  };

  const handleOpenDrawer = (event?: React.MouseEvent<HTMLDivElement>) => {
    // !showDefaultChatMessage &&
    event!.stopPropagation();
    setIsOpen(!isOpen);
    if (!isOpen) {
      document.addEventListener("click", handleClickOutside);
    } else {
      document.removeEventListener("click", handleClickOutside);
    }
    // if (containerRef.current) {
    //   containerRef.current.scrollTop = 0;
    // }
  };

  useEffect(() => {
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);
  const onClose = () => {
    setIsOpen(false);
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  };
  const handleClickOutside = (event: MouseEvent) => {
    if (
      chatDatasourcesRef.current &&
      !chatDatasourcesRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false);
    }
  };

  return (
    <StyledContainer $isOpen={isOpen} $isHistoryCollapsed={historyCollapsed!}>
      {isOpen && (
        <div ref={chatDatasourcesRef}>
          <ChatDatasourcesList
            setSelectedDatasources={setSelectedDatasources}
            selectedDatasources={selectedDatasources}
            setDatasetsFilters={setDatasetsFilters}
            datasources={datasources?.content || []}
            IsLoading={IsLoading}
            datasetsFilters={datasetsFilters}
            isOpen={isOpen}
          />
        </div>
      )}
      {isLoadingConversation && !isLoadingMore ? (
        <LoadingComponent />
      ) : (
        <div className="content" ref={messagesRef}>
          <HeaderContainer>
            {/* <ChatEntrepriseConnectorsHeader /> */}

            {(selectedDatasources.length > 0 ||
              selectedConversation?.dataSources) && (
              <>
                <ChatDatasourceHeader
                  selectedDatasources={selectedDatasources}
                  selectedConversation={selectedConversation}
                  setSelectedDatasources={setSelectedDatasources}
                  datasources={datasources?.content || []}
                />
              </>
            )}
            {showDefaultChatMessage && (
              <DefaultChatMessage
                stepRef7={stepRef7}
                selectedOption={selectedOption}
                setMessagePayLoad={setMessagePayLoad}
                messagePayLoad={messagePayLoad}
                datasources={datasources?.content || []}
                setSelectedDatabase={setSelectedDatabase}
                setSelectedDatasources={setSelectedDatasources}
                selectedDatasources={selectedDatasources}
                setDatasetsFilters={setDatasetsFilters}
                IsLoading={IsLoading}
                // selectedDatabase={selectedDatabase}
                // setSelectedWebsite={setSelectedWebsite}
                // selectedWebsite={selectedWebsite}
              />
            )}
          </HeaderContainer>
          {isLoadingMore && <Loading />}
          <span ref={reachTheEndRef} />
          <ContentContainer>
            {conversation?.content
              ?.map((conversation, index) => (
                <div key={index}>
                  {conversation && conversation.input && (
                    <ChatQuestions message={conversation.input} />
                  )}
                  {conversation && !conversation.response && <ChatLoading />}
                  {conversation && conversation.response && (
                    <ChatResponse message={conversation.response} />
                  )}
                </div>
              ))
              .reverse()}
          </ContentContainer>
          <span ref={bottomMessagesRef} />
        </div>
      )}
      <div className="position">
        <ChatInput
          stepRef6={stepRef6}
          onMessageSend={handleMessageSend}
          selectedOption={selectedOption}
          setSelectedOption={setSelectedOption}
          setMessagePayLoad={setMessagePayLoad}
          messagePayLoad={messagePayLoad}
          handleOpenDrawer={handleOpenDrawer}
          setIsOpen={setIsOpen}
          setSelectedDatasources={setSelectedDatasources}
          selectedDatasources={selectedDatasources}
          selectedConversation={selectedConversation}

          // setSelectedDatabase={setSelectedDatabase}
          // selectedDatabase={selectedDatabase}
        />
      </div>
    </StyledContainer>
  );
};

export default DiscussionSearch;

const StyledContainer = styled.div<{
  $isOpen: boolean;
  $isHistoryCollapsed: boolean;
}>`
  .ant-drawer-inline {
    position: relative !important;
  }
  .ant-drawer-top > .ant-drawer-content-wrapper {
    height: fit-content !important;
    border-radius: 40px !important;
  }
  .ant-drawer .ant-drawer-content {
    border-radius: 40px !important;
  }
  // height: 70%;
  height: calc(100vh - 213px);

  display: flex;
  flex-direction: column;
  border-radius: 20px;
  background: #eff1fd;
  padding: 0px 30px 20px 30px;
  margin: 40px 20px 20px 0px;
  ${(p) => !p.$isHistoryCollapsed && `max-width: 67vw;`}
  .position {
    margin-top: auto;
  }
  .content {
    overflow-y: auto;
    padding-right: 8px;
    ${scrollBarStyles}
  }
`;
const StyledDataWeb = styled.div`
  display: flex;
  flex-direction: row;
  gap: 29px;
`;
const HeaderContainer = styled.div`
  position: sticky;
  width: 100%;
  background: #eff1fd;
  top: -2px;
  z-index: 1;
`;
const ContentContainer = styled.div`
  margin-top: 20px;
`;
